import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, MailCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../context/I18nContext";

export default function ForgotPassword() {
  const { t } = useI18n();
  const [account, setAccount] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/auth/forgot-password", { account: account.trim() });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-cream">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} className="w-full max-w-md">
        <div className="font-sans text-xl tracking-[0.25em] mb-10">OMNIA</div>
        {sent ? (
          <div data-testid="forgot-sent" className="space-y-4">
            <MailCheck className="w-8 h-8 text-violet" />
            <h2 className="text-2xl font-medium tracking-tight">{t("auth.checkEmailTitle")}</h2>
            <p className="text-navy/60">{t("auth.checkEmailBody")}</p>
            <Link to="/login" className="text-violet hover:underline inline-block">{t("auth.backToLogin")}</Link>
          </div>
        ) : (
          <>
            <div className="overline text-navy/40 mb-3">{t("auth.recoverAccess")}</div>
            <h2 className="text-3xl font-medium tracking-tight mb-2">{t("auth.forgotQ")}</h2>
            <p className="text-navy/60 mb-8">{t("auth.forgotSub")}</p>
            <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
              <input data-testid="forgot-account" value={account} onChange={(e) => setAccount(e.target.value)} required
                autoCapitalize="none" placeholder={t("auth.accountPlaceholder")}
                className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
              {error && <p data-testid="forgot-error" className="text-sm text-destructive">{error}</p>}
              <button data-testid="forgot-submit" type="submit" disabled={loading}
                className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy text-white py-3.5 hover:bg-navy-soft transition-colors disabled:opacity-60">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("auth.sendLink")}
                {!loading && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
              </button>
            </form>
            <Link to="/login" className="mt-6 inline-block text-sm text-navy/50 hover:text-navy">{t("auth.backToLogin")}</Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
